import { navigate } from 'gatsby';
import { useEffect, useMemo, useState } from 'react';

import {
  generateLocationQueryString,
  getLocationQueryStringParam,
} from '~/utils/browser';
import { QUERY_STRING_PARAM_STRATEGY } from '~/utils/constants';

import { UseLoadMoreParams } from './models';

export const useLoadMore = <T extends unknown>({
  componentId,
  items,
  itemsPerPage,
}: UseLoadMoreParams<T>) => {
  let queryParam = `page`;
  if (componentId) {
    queryParam = `${componentId}-${queryParam}`;
  }
  const [visibleItems, setVisibleItems] = useState(items);

  const queryParametersPage =
    (getLocationQueryStringParam(
      queryParam,
      QUERY_STRING_PARAM_STRATEGY.STRING
    ) as string) || '1';

  const [page, setPage] = useState<number>(parseInt(queryParametersPage, 10));

  const nextPageButtonVisible = useMemo(
    () =>
      itemsPerPage !== undefined &&
      page < Math.ceil(items.length / itemsPerPage),
    [items, itemsPerPage, page]
  );

  useEffect(() => {
    if (page > 1) {
      navigate(generateLocationQueryString({ page: page.toString() }), {
        state: { noScroll: true },
      });
    }
  }, [page]);

  useEffect(() => {
    if (itemsPerPage) {
      setVisibleItems(items.slice(0, page * itemsPerPage));

      return;
    }

    setVisibleItems(items);
  }, [items, itemsPerPage, page]);

  const nextPage = () => {
    setPage((current) => current + 1);
  };

  const previousPage = () => {
    setPage((current) => current - 1);
  };

  return {
    visibleItems,
    nextPageButtonVisible,
    nextPage,
    previousPage,
    setPage,
  };
};
